import { Component, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AuthService, ModalService } from '@app/core/services';
import { ConfigService } from '@app/core/services/config.service';
import { loadAnalyticReports, loadUserRoles } from '@app/core/store/actions/admin.actions';
import { loadRestrictedAnalytesLookup } from '@app/core/store/actions/results.actions';
import { AppState } from '@app/core/store/reducers';
import { selectSpinnerState } from '@app/core/store/reducers/spinners.reducer';
import { selectUsersState } from '@app/core/store/reducers/users.reducer';
import { MessageModalComponent } from '@app/shared/message-modal.component';
import { User } from '@core/models';
import { UsersService } from '@core/services/users.service';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { isURLLink } from './../../main';
import { EulaModalComponent } from './eula-modal.component';

@Component({
  templateUrl: './portal.component.html',
  host: { class: 'd-flex flex-column vh-100' } //Styles needed for layout
})
export class PortalComponent implements OnInit, OnDestroy {
  spinnerState = false;
  wasRedirectedOnce = false;
  eulaText: string;
  timeoutWarningSubscription: Subscription;
  timeoutSubscription: Subscription;
  user: User;
  eulaModal: EulaModalComponent;

  currentUrl: string;
  checkEulaOnce = false;

  revokePath: string;
  usersStateSubs: Subscription;

  constructor(private store: Store<AppState>, private configService: ConfigService, private idle: Idle,
    private keepalive: Keepalive, private modalService: ModalService,
    private router: Router, private usersService: UsersService, private authService: AuthService
  ) {

    store.select(selectSpinnerState).pipe(takeUntilDestroyed()).subscribe(s => {
      this.spinnerState = s.status;
    });
    this.revokePath = this.configService.appConfig.apic.baseUrl + this.configService.appConfig.apic.revokePath;
  }

  ngOnInit(): void {
    this.store.select(selectUsersState).subscribe((usersState) => {
      if (usersState && usersState.user) {
        this.user = usersState.user;
        if (this.user && !this.wasRedirectedOnce) {

          if (this.user.eulaAcceptedTime == null && !this.checkEulaOnce) {
            this.checkEulaOnce = true;
            this.usersService.getEula().then((eulaText) => {
              const modalRef = this.modalService.open(EulaModalComponent, { size: 'xl', backdropClass: 'modal-dark-backdrop' });
              this.eulaModal = modalRef.componentInstance as EulaModalComponent;
              this.eulaModal.eulaText = eulaText;
              this.eulaModal.user = this.user;
              modalRef.result.then(result => {
                this.modalService.dismissAll();
                this.routeToLandingPage();
              });
            });
          } else {
            this.routeToLandingPage();
          }

        }
      }
    });
  }

  routeToLandingPage(): void {
    this.store.dispatch(loadUserRoles());
    this.store.dispatch(loadAnalyticReports());
    this.store.dispatch(loadRestrictedAnalytesLookup());

    let redirectUrl = '/results/search';
    const pathArray = this.router.url.split('/');

    if (this.user.isLCAAdmin) {
      redirectUrl = '/organizations';
    } else if (this.user.isCustomerAdmin) {
      redirectUrl = '/users';
    } else if (this.user.isUser && !this.user.allowSearch) {
      if (this.user.isGapsInCareUser) {
        redirectUrl = '/analytic-reports/gaps-in-care';
      } else if (this.user.isInsightAnalyticUser) {
        redirectUrl = '/analytic-reports/insight-analytics';
      } else {
        redirectUrl = '/setup-required';
      }
    }

    const match = pathArray.filter((v) => ['search-results', 'search'].includes(v));
    if (pathArray?.length === 5 && match) {
      redirectUrl = '/results/' + match + '/' + pathArray[4];
    }

    if (isURLLink && pathArray.indexOf('preferences') !== -1) {
      redirectUrl = '/preferences/user-preferences';
    }

    this.configureIdleActions();
    this.router.navigate([`/portal${redirectUrl}`]);
    this.wasRedirectedOnce = true;
  }

  configureIdleActions(): void {
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.setIdle(this.configService.appConfig.idle.secondsToWarning);
    this.idle.setTimeout(this.configService.appConfig.idle.secondsToTimeout);
    this.keepalive.interval(this.configService.appConfig.idle.keepAliveInterval);

    this.timeoutWarningSubscription = this.idle.onTimeoutWarning.subscribe(() => {
      if (!this.modalService.hasOpenModals() && this.idle.isRunning) {
        const modalRef = this.modalService.open(MessageModalComponent);
        modalRef.componentInstance.modalText =
          'Your session is expiring due to inactivity. Would you like to continue your session?';
        modalRef.componentInstance.title = 'Session Expiring';
        modalRef.componentInstance.labelConfirm = 'Continue';
        modalRef.componentInstance.labelCancel = 'Logout';
        modalRef.result.then(result => {
          this.modalService.dismissAll();
          if (result) {
            this.idle.watch();
          } else {
            this.authService.signOut();
          }
        });
      }
    });

    this.timeoutSubscription = this.idle.onTimeout.subscribe(() => {
      this.modalService.dismissAll();
      this.authService.signOut();
    });

    this.idle.watch();
  }

  ngOnDestroy(): void {
    if (this.timeoutWarningSubscription) this.timeoutWarningSubscription.unsubscribe();
    if (this.timeoutSubscription) this.timeoutSubscription.unsubscribe();
    if (this.usersStateSubs) this.usersStateSubs.unsubscribe();
  }

}
